import { createContext, useContext, useState, useEffect, useCallback } from "react";

const ADMIN_EMAIL    = "sharmadeepika85.ds@gmail.com";
const ADMIN_PASSWORD = "nitin21212";

const TOKEN_KEY = "pdfstudio_token";
const USER_KEY  = "pdfstudio_user";

const AuthContext = createContext(null);

// ── Token helpers ─────────────────────────────────────────────────────────────

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY) || null;
}

function storeToken(token) {
  if (token) localStorage.setItem(TOKEN_KEY, token);
}

function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

// ── Authenticated fetch helper ────────────────────────────────────────────────

export async function authFetch(url, options = {}) {
  const token = getStoredToken();
  return fetch(url, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(options.headers || {}),
    },
  });
}

// ── Safe JSON parser ───────────────────────────────────────────────────────────

async function parseJSON(res) {
  const text = await res.text();
  try { return text ? JSON.parse(text) : {}; }
  catch { throw new Error("Invalid JSON from server"); }
}

// ── User enricher ─────────────────────────────────────────────────────────────

function enrichUser(rawUser, email, password) {
  const isAdminLogin = email?.toLowerCase() === ADMIN_EMAIL && password === ADMIN_PASSWORD;
  const proExpired =
    rawUser.proExpiresAt && new Date(rawUser.proExpiresAt) < new Date();
  // Also handle snake_case from legacy PHP responses
  const proExpiresAt = rawUser.proExpiresAt || rawUser.pro_expires_at || rawUser.pro_end_date || null;
  const proExpiredAlt = proExpiresAt && new Date(proExpiresAt) < new Date();

  return {
    ...rawUser,
    name: rawUser.name || rawUser.full_name || email,
    role: isAdminLogin ? "admin" : rawUser.role || "user",
    isAdmin: isAdminLogin || rawUser.role === "admin" || rawUser.isAdmin || false,
    plan: (rawUser.plan === "pro" && (proExpired || proExpiredAlt)) ? "free" : rawUser.plan || "free",
    proExpiresAt: proExpiresAt,
    pro_end_date: proExpiresAt, // keep legacy field too
  };
}

// ── Auth registry (admin panel cross-device sync) ─────────────────────────────

function syncToRegistry(userData) {
  try {
    const raw = localStorage.getItem("pdfstudio_users_registry");
    const registry = raw ? JSON.parse(raw) : [];
    const idx = registry.findIndex((u) => u.email === userData.email);
    const entry = { ...userData, last_seen: new Date().toISOString() };
    if (idx >= 0) registry[idx] = { ...registry[idx], ...entry };
    else registry.push({ ...entry, created_at: new Date().toISOString() });
    localStorage.setItem("pdfstudio_users_registry", JSON.stringify(registry));
  } catch {}
}

// ── AuthProvider ──────────────────────────────────────────────────────────────

export function AuthProvider({ children }) {
  const [user, setUser]       = useState(null);
  const [loading, setLoading] = useState(true);

  // Restore session on mount
  useEffect(() => {
    const saved = localStorage.getItem(USER_KEY);
    if (saved) {
      try { setUser(JSON.parse(saved)); } catch {}
    }
    setLoading(false);

    // Track session start time
    localStorage.setItem("pdfstudio_session_start", Date.now().toString());
  }, []);

  const persistUser = useCallback((u) => {
    setUser(u);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    syncToRegistry(u);
  }, []);

  // ── Session duration tracking ──────────────────────────────────────────────

  const flushSessionDuration = useCallback(async () => {
    const startStr = localStorage.getItem("pdfstudio_session_start");
    if (!startStr) return;
    const minutes = Math.round((Date.now() - parseInt(startStr, 10)) / 60000);
    localStorage.removeItem("pdfstudio_session_start");
    if (minutes > 0 && getStoredToken()) {
      try {
        await authFetch("/api/payment/session", {
          method: "POST",
          body: JSON.stringify({ minutes }),
        });
      } catch {}
    }
  }, []);

  // ── Login ──────────────────────────────────────────────────────────────────

  const login = useCallback(async (email, password) => {
    setLoading(true);
    try {
      // Try Express backend first; fall back to legacy PHP if unavailable
      let data = null;
      let token = null;

      let expressUnavailable = false;
      try {
        const res = await fetch("/api/auth/login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const json = await parseJSON(res);
        if (res.ok && json.success) {
          data  = json.user || {};
          token = json.token || null;
        } else if (res.status === 404 || res.status === 502 || res.status === 503) {
          // Express not running / not reachable — try PHP fallback
          expressUnavailable = true;
        } else {
          // Legitimate auth error from Express (wrong password, etc.)
          throw new Error(json.message || "Invalid email or password");
        }
      } catch (e) {
        // Network error means Express backend is down — try PHP
        if (!expressUnavailable && e.message !== "Invalid email or password" && !e.message.includes("Login failed")) {
          expressUnavailable = true;
        } else if (!expressUnavailable) {
          throw e;
        }
      }

      if (expressUnavailable) {
        // Fall back to PHP endpoint
        const res = await fetch("/api/login.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email, password }),
        });
        const json = await parseJSON(res);
        if (!res.ok || json.error) throw new Error(json.error || "Invalid email or password");
        data  = json.user || json || {};
        token = json.token || null;
      }

      if (token) storeToken(token);
      const enriched = enrichUser(data, email, password);
      persistUser(enriched);
      localStorage.setItem("pdfstudio_session_start", Date.now().toString());
      return { user: enriched };
    } finally {
      setLoading(false);
    }
  }, [persistUser]);

  // ── Signup ─────────────────────────────────────────────────────────────────

  const signup = useCallback(async (name, email, password) => {
    setLoading(true);
    try {
      let data = null;
      let token = null;

      let expressUnavailable = false;
      try {
        const res = await fetch("/api/auth/signup", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, email, password }),
        });
        const json = await parseJSON(res);
        if (res.ok && json.success) {
          data  = json.user || {};
          token = json.token || null;
        } else if (res.status === 404 || res.status === 502 || res.status === 503) {
          expressUnavailable = true;
        } else {
          throw new Error(json.message || "Signup failed");
        }
      } catch (e) {
        if (!expressUnavailable && !e.message.includes("Signup failed") && !e.message.includes("already")) {
          expressUnavailable = true;
        } else if (!expressUnavailable) {
          throw e;
        }
      }

      if (expressUnavailable) {
        // Fall back to PHP endpoint
        const res = await fetch("/api/register.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name, email, password }),
        });
        const json = await parseJSON(res);
        if (!res.ok || json.error) throw new Error(json.error || "Signup failed");
        // Auto-login after PHP signup
        return await login(email, password);
      }

      if (token) storeToken(token);
      const enriched = enrichUser(data, email, password);
      persistUser(enriched);
      localStorage.setItem("pdfstudio_session_start", Date.now().toString());
      return { user: enriched };
    } finally {
      setLoading(false);
    }
  }, [persistUser, login]);

  // ── Logout ─────────────────────────────────────────────────────────────────

  const logout = useCallback(async () => {
    await flushSessionDuration();
    clearToken();
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem("pdfstudio_session_start");
    setUser(null);
  }, [flushSessionDuration]);

  // ── Refresh user from backend ──────────────────────────────────────────────

  const refreshUser = useCallback(async () => {
    if (!getStoredToken()) {
      const saved = localStorage.getItem(USER_KEY);
      if (saved) {
        try { setUser(JSON.parse(saved)); } catch {}
      }
      return;
    }
    try {
      const res = await authFetch("/api/auth/me");
      const data = await parseJSON(res);
      if (res.ok && data.success) {
        const enriched = enrichUser(data.user || {}, user?.email, null);
        persistUser(enriched);
      }
    } catch {}
  }, [user?.email, persistUser]);

  // ── Update user profile ────────────────────────────────────────────────────

  const updateUser = useCallback(async (updates) => {
    const updated = enrichUser({ ...user, ...updates }, user?.email, null);
    persistUser(updated);

    if (updates.name && user?.email && getStoredToken()) {
      try {
        await authFetch("/api/auth/me", {
          method: "PATCH",
          body: JSON.stringify({ name: updates.name }),
        });
      } catch {}
    }
    return updated;
  }, [user, persistUser]);

  // ── Upgrade to Pro (called after Razorpay verification) ───────────────────

  const upgradeUserToPro = useCallback(async ({ razorpay_order_id, razorpay_payment_id, razorpay_signature }) => {
    if (!getStoredToken()) throw new Error("Not authenticated");

    const res = await authFetch("/api/payment/verify", {
      method: "POST",
      body: JSON.stringify({ razorpay_order_id, razorpay_payment_id, razorpay_signature }),
    });
    const data = await parseJSON(res);
    if (!res.ok || !data.success) throw new Error(data.message || "Payment verification failed");

    const updated = enrichUser({
      ...user,
      plan: "pro",
      proExpiresAt: data.pro_expires_at,
      pro_end_date: data.pro_expires_at,
    }, user?.email, null);
    persistUser(updated);
    return updated;
  }, [user, persistUser]);

  return (
    <AuthContext.Provider value={{
      user, loading,
      login, signup, logout,
      refreshUser, updateUser, upgradeUserToPro,
      flushSessionDuration,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
